<script setup>

import { ref, computed, inject, watch, nextTick } from 'vue';

import Zone from './Zone.vue';
import Selecteur from './Selecteur.vue';
import Texte from './Texte.vue';
import Tableau from './Tableau.vue';
import Graphique from './Graphique.vue';
import SousSection from './SousSection.vue';
import BoutonMenuActions from './BoutonMenuActions.vue';
import { getPositionBriqueRapport } from '@/scripts/global.js';
import { getAppelApiGlisserDeplacer } from '@/scripts/gestionGlisserDeplacer.js';

const trouverElementEtParent = inject('trouverElementEtParent');
const selectionnerElement = inject('selectionnerElement');
const elementsSelectionnes = inject('elementsSelectionnes');
const selectionnerSection = inject('selectionnerSection');
const getElementParent = inject('getElementParent');
const basculerSelection = inject('basculerSelection');
const classeMasquage = inject('classeMasquage');
const appelerApiModifModele = inject('appelerApiModifModele');
const donnerFocusAuRapport = inject('donnerFocusAuRapport');

const { element, parent } = defineProps({
	element: Object,
	parent: Object
});

const modeEdition = ref(false);
const composantElement = ref(null);

const classesConteneur = computed(() => {
	const classes = {
		'align-debut': element.alignement === 'DEBUT',
		'align-centre': element.alignement === 'CENTRE',
		'align-fin': element.alignement === 'FIN',
		'largeur-max': element.largeur === 'MAX',		
	};
	if (element.masque && !element.selectionne) {
		classes[classeMasquage.value] = true;
	} else {
		classes[classeMasquage.value] = false;
	}
	return classes;
});

const classesElement = computed(() => {
	return {
		selectionne: element.selectionne,
		survol: element.survol,
		zone: element.type === 'ZONE',
		'largeur-max': element.largeur === 'MAX',
		'largeur-min': element.largeur === 'MIN'
	};
});

const stylesElement = computed(() => {
	const styles = {};
	if (element.decalage) {
		const proprieteMarge = (parent?.orientation === 'VERTICALE' ? 'margin-top' : 'margin-left');
		styles[proprieteMarge] = `${element.decalage}px`;
	}
	return styles;
});

function activerSurvol(element) {
	if (!element.selectionne) {
		element.survol = true;
	}
}

function desactiverSurvol(element) {
	if (element.survol) {
		delete element.survol;
	}
}

watch(() => element.selectionne, (sel) => {
	if (sel) {
		composantElement.value.scrollIntoView({behaviour: "smooth", block: "nearest", inline: "nearest"});
	}
});

watch(() => element.modeEdition, (edition) => {
	if (edition) {
		switch (element.type) {
			case 'SECTION':
				selectionnerSection(element.positionSection);
				selectionnerElement(element.titre, element);
				break;
			case 'ZONE':
			case 'TABLEAU':
				element.selectionPremierElement = true;
				break;
			case 'TEXTE':
			case 'SELECTEUR':
				modeEdition.value = true;
				break;
			case 'GRAPHIQUE':
				modeEdition.value = true;
				element.selectionPremierElement = true;
				break;
		}
		delete element.modeEdition;
	}
});

watch(() => element.selectionElementParent, (sel) => {
	if (sel) {
		const elementEtParentSuperieur = getElementParent(element);
			if (elementEtParentSuperieur != null) {
				if (elementEtParentSuperieur.element.type === 'SECTION') {
					if (elementEtParentSuperieur.element.positionSection.length > 0) {
						selectionnerSection(elementEtParentSuperieur.parent.positionSection);
						nextTick(() => {
							selectionnerElement(elementEtParentSuperieur.element, elementEtParentSuperieur.parent);
						});
					}
				} else {
					selectionnerElement(elementEtParentSuperieur.element, elementEtParentSuperieur.parent);
				}
			}
		delete element.selectionElementParent;
	}
});

function activerModeEdition() {
	modeEdition.value = true;
}

function desactiverModeEdition() {
	modeEdition.value = false;
	donnerFocusAuRapport();
}

function commencerGlisserDeplacer(event) {
	event.stopPropagation();
	var positionsBriquesDeplacees;
	if (element.selectionne) {
		positionsBriquesDeplacees = elementsSelectionnes.value.map(elt => getPositionBriqueRapport(elt, elt.elementParent)).reverse();
	} else {
		positionsBriquesDeplacees = [getPositionBriqueRapport(element, parent)];
	}
	const donneesTransfert = {
		type: 'DEPLACEMENT_BRIQUES',
		positionsBriquesDeplacees: positionsBriquesDeplacees
	};
	event.dataTransfer.setData('text/plain', JSON.stringify(donneesTransfert));
	event.dataTransfer.dropEffect = 'move';
}

function accueillirGlisserDeplacer(event) {
	const appelApi = getAppelApiGlisserDeplacer(event, element, parent);
	if (appelApi) {
		appelerApiModifModele(appelApi.api, appelApi.argument, (reponseOK) => {
			if (reponseOK) {
				const elementEtParent = trouverElementEtParent(element.positionSection, element.positionDansSection);
				selectionnerElement(elementEtParent.element, elementEtParent.parent);
			}
		});		
	}
}

</script>

<template>
	<div class="conteneur-element-rapport" 
		ref="composantElement"
		:draggable="element.selectionne && !modeEdition"
		@dragstart.stop="commencerGlisserDeplacer($event)"
		@dragenter.stop.prevent="activerSurvol(element)"
		@dragover.stop.prevent="activerSurvol(element)"
		@dragleave.stop.prevent="desactiverSurvol(element)"
		@drop.stop.prevent="accueillirGlisserDeplacer"
		:class="classesConteneur">
			<div class="element-rapport" 
			:class="classesElement" 
			:style="stylesElement" 
			@mousedown.exact.stop="selectionnerElement(element, parent)" 
			@mousedown.ctrl.exact.stop="basculerSelection(element, parent)"
			@mouseover.stop="activerSurvol(element)"
			@mouseout.stop="desactiverSurvol(element)">
				<Zone v-if="element.type === 'ZONE'" 
					:zone="element" />
				<Selecteur v-else-if="element.type === 'SELECTEUR'" 
					:selecteur="element" 
					:mode-edition="modeEdition"
					@activationModeEdition="activerModeEdition()"
					@desactivationModeEdition="desactiverModeEdition()" />
				<Texte v-else-if="element.type === 'TEXTE'" 
					:zone="parent"
					:texte="element" 
					:mode-edition="modeEdition" 
					@activationModeEdition="activerModeEdition()"
					@desactivationModeEdition="desactiverModeEdition()" />
				<Tableau v-else-if="element.type === 'TABLEAU'" 
					:tableau="element" />
				<Graphique v-else-if="element.type === 'GRAPHIQUE'"
					:graphique="element" 
					:modeEdition="modeEdition"
					@activationModeEdition="activerModeEdition()"
					@desactivationModeEdition="desactiverModeEdition()" />
				<SousSection v-else-if="element.type === 'SECTION'" 
					:section="element"
					@selection-section="selectionnerSection(element.positionSection)" />
				<BoutonMenuActions 
					v-if="element.dernierElementSelectionne && !modeEdition"
					:element="element" 
					:style="{top: '-6px', left: '7px'}" />
			</div>
	</div>	
</template>

<style scoped>

.conteneur-element-rapport {
	display: flex;
	scroll-margin: 10px;
}

.element-rapport {
	display: flex;
	border: solid 1px transparent;
	border-radius: 4px;
	padding: 6px;
}


</style>